/**
 * SubtasksSection — displays subtask list with progress bar, completion
 * toggle, drag-to-reorder, delete, and inline creation within CardDetailModal.
 *
 * Supports:
 * - Progress bar with color coding (green=all done, blue=partial, gray=none)
 * - Drag-to-reorder subtasks (persists via childSort field)
 * - Interactive checkbox to toggle subtask completion (moves card to/from done list)
 * - Delete button to remove subtasks
 * - Inline text input for creating new subtasks (Enter to submit)
 * - Read-only mode hides creation input, drag handles, and action buttons
 * - Keyboard navigation and screen reader announcements
 */

import * as React from 'react';
import { Checkbox } from '../ui/checkbox';
import { cn } from '../../lib/utils';
import * as api from '../../services/api-client';
import { useActiveBoard } from '../../state/active-board-context';
import { CardAssigneePopover, CardAssignee } from './CardAssigneePopover';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../ui/alert-dialog';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SubtaskAssignee {
  readonly userId: string;
  readonly displayName: string;
}

export interface SubtaskResponseItem {
  readonly cardId: string;
  readonly cardNumber: number;
  readonly title: string;
  readonly listId: string;
  readonly listName: string;
  readonly assignees: SubtaskAssignee[];
  readonly isComplete: boolean;
}

export interface SubtasksSectionProps {
  readonly boardId: string;
  readonly cardId: string;
  readonly subtasks: SubtaskResponseItem[];
  readonly progress: { completed: number; total: number };
  readonly isReadOnly: boolean;
  readonly onSubtaskCreated: (subtask: { cardId: string; cardNumber: number; title: string }) => void;
  readonly onSubtaskToggled: (subtaskCardId: string, isComplete: boolean) => void;
  readonly onSubtaskDeleted: (subtaskCardId: string) => void;
  readonly onSubtasksReordered?: (reorderedSubtasks: SubtaskResponseItem[]) => void;
  readonly onSubtaskRenamed?: (subtaskCardId: string, newTitle: string) => void;
  /** Current parent card assignees — used to auto-add subtask assignees to parent */
  readonly parentAssignees?: readonly CardAssignee[];
  /** Called when the parent card's members should be updated (auto-add/remove from subtask assignment) */
  readonly onParentAssigneesChange?: (assignees: CardAssignee[]) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getProgressPercentage(completed: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((completed / total) * 100);
}

function getProgressBarColor(completed: number, total: number): string {
  if (total === 0) return 'bg-muted-foreground/30';
  if (completed === total) return 'bg-green-600';
  if (completed > 0) return 'bg-blue-600';
  return 'bg-muted-foreground/30';
}

function getInitials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/);
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  if (parts.length === 1) return first.charAt(0).toUpperCase();
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
}

// ---------------------------------------------------------------------------
// SubtasksSection
// ---------------------------------------------------------------------------

export function SubtasksSection({
  boardId,
  cardId,
  subtasks,
  progress,
  isReadOnly,
  onSubtaskCreated,
  onSubtaskToggled,
  onSubtaskDeleted,
  onSubtasksReordered,
  onSubtaskRenamed,
  parentAssignees,
  onParentAssigneesChange,
}: SubtasksSectionProps): React.ReactElement {
  const { state: boardState } = useActiveBoard();
  const boardMembers = React.useMemo(() =>
    (boardState.board?.members ?? []).map((m) => ({
      userId: m.userId,
      displayName: m.displayName,
      email: '',
    })),
    [boardState.board?.members],
  );

  const [newTitle, setNewTitle] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Drag state for reordering
  const [draggingId, setDraggingId] = React.useState<string | null>(null);
  const [dragOverId, setDragOverId] = React.useState<string | null>(null);

  // Inline subtask title editing state
  const [editingSubtaskId, setEditingSubtaskId] = React.useState<string | null>(null);
  const [editingSubtaskValue, setEditingSubtaskValue] = React.useState('');
  const subtaskInputRef = React.useRef<HTMLInputElement>(null);

  // Track subtask assignees locally so popover changes reflect immediately
  const [subtaskAssignees, setSubtaskAssignees] = React.useState<Record<string, CardAssignee[]>>(() => {
    const map: Record<string, CardAssignee[]> = {};
    for (const s of subtasks) {
      map[s.cardId] = s.assignees.map((a) => ({ userId: a.userId, displayName: a.displayName }));
    }
    return map;
  });

  // Sync when subtasks prop changes (e.g., new subtask created)
  React.useEffect(() => {
    setSubtaskAssignees((prev) => {
      const next: Record<string, CardAssignee[]> = {};
      for (const s of subtasks) {
        next[s.cardId] = prev[s.cardId] ?? s.assignees.map((a) => ({ userId: a.userId, displayName: a.displayName }));
      }
      return next;
    });
  }, [subtasks]);

  // State for the "remove from parent?" confirmation
  const [removeFromParentPrompt, setRemoveFromParentPrompt] = React.useState<{ userId: string; displayName: string } | null>(null);

  const handleSubtaskAssignmentChange = React.useCallback((subtaskCardId: string, assignees: CardAssignee[]) => {
    const previousAssignees = subtaskAssignees[subtaskCardId] ?? [];
    setSubtaskAssignees((prev) => ({ ...prev, [subtaskCardId]: assignees }));

    // Detect added members — auto-add to parent card
    if (onParentAssigneesChange && parentAssignees) {
      const addedMembers = assignees.filter(
        (a) => !previousAssignees.some((p) => p.userId === a.userId),
      );
      const parentIds = new Set(parentAssignees.map((p) => p.userId));
      const newForParent = addedMembers.filter((a) => !parentIds.has(a.userId));

      if (newForParent.length > 0) {
        onParentAssigneesChange([...parentAssignees, ...newForParent]);
      }
    }

    // Detect removed members — ask before removing from parent
    if (onParentAssigneesChange && parentAssignees) {
      const removedMembers = previousAssignees.filter(
        (p) => !assignees.some((a) => a.userId === p.userId),
      );
      for (const removed of removedMembers) {
        // Only ask if they're on the parent card
        if (parentAssignees.some((pa) => pa.userId === removed.userId)) {
          setRemoveFromParentPrompt(removed);
          break; // Handle one at a time
        }
      }
    }
  }, [subtaskAssignees, parentAssignees, onParentAssigneesChange]);

  const handleConfirmRemoveFromParent = React.useCallback(() => {
    if (!removeFromParentPrompt || !parentAssignees || !onParentAssigneesChange) return;
    onParentAssigneesChange(
      parentAssignees.filter((p) => p.userId !== removeFromParentPrompt.userId),
    );
    setRemoveFromParentPrompt(null);
  }, [removeFromParentPrompt, parentAssignees, onParentAssigneesChange]);

  const handleDismissRemoveFromParent = React.useCallback(() => {
    setRemoveFromParentPrompt(null);
  }, []);

  React.useEffect(() => {
    if (editingSubtaskId && subtaskInputRef.current) {
      subtaskInputRef.current.focus();
      subtaskInputRef.current.select();
    }
  }, [editingSubtaskId]);

  const startSubtaskEdit = React.useCallback((subtaskCardId: string, currentTitle: string) => {
    if (isReadOnly || !onSubtaskRenamed) return;
    setEditingSubtaskId(subtaskCardId);
    setEditingSubtaskValue(currentTitle);
  }, [isReadOnly, onSubtaskRenamed]);

  const saveSubtaskTitle = React.useCallback(() => {
    if (!editingSubtaskId) return;
    const trimmed = editingSubtaskValue.trim();
    const subtask = subtasks.find((s) => s.cardId === editingSubtaskId);
    setEditingSubtaskId(null);
    if (trimmed && subtask && trimmed !== subtask.title && onSubtaskRenamed) {
      onSubtaskRenamed(editingSubtaskId, trimmed);
    }
  }, [editingSubtaskId, editingSubtaskValue, subtasks, onSubtaskRenamed]);

  const cancelSubtaskEdit = React.useCallback(() => {
    setEditingSubtaskId(null);
    setEditingSubtaskValue('');
  }, []);

  const handleSubtaskTitleKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveSubtaskTitle();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelSubtaskEdit();
    }
  }, [saveSubtaskTitle, cancelSubtaskEdit]);

  const percentage = getProgressPercentage(progress.completed, progress.total);
  const barColor = getProgressBarColor(progress.completed, progress.total);
  const progressText = `${progress.completed} of ${progress.total} subtasks done`;

  // ---------------------------
  // Drag-to-reorder handlers
  // ---------------------------

  const handleDragStart = (subtaskId: string) => {
    setDraggingId(subtaskId);
  };

  const handleDragOver = (e: React.DragEvent, subtaskId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (subtaskId !== draggingId) {
      setDragOverId(subtaskId);
    }
  };

  const handleDragLeave = () => {
    setDragOverId(null);
  };

  const handleDrop = async (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    setDragOverId(null);

    if (!draggingId || draggingId === targetId) {
      setDraggingId(null);
      return;
    }

    // Compute new order
    const oldIndex = subtasks.findIndex((s) => s.cardId === draggingId);
    const newIndex = subtasks.findIndex((s) => s.cardId === targetId);
    if (oldIndex < 0 || newIndex < 0) {
      setDraggingId(null);
      return;
    }

    const reordered = [...subtasks];
    const [moved] = reordered.splice(oldIndex, 1);
    if (!moved) { setDraggingId(null); return; }
    reordered.splice(newIndex, 0, moved);

    // Optimistically update parent state
    onSubtasksReordered?.(reordered);
    setDraggingId(null);

    // Persist: assign numeric childSort values to ALL subtasks in the new order.
    // This ensures consistent numeric sorting on re-fetch (avoids mixed
    // numeric/ISO-string comparisons that break lexicographic sort).
    const SORT_GAP = 1024;

    try {
      await Promise.all(
        reordered.map((subtask, index) =>
          api.put(`/v1/boards/${boardId}/cards/${subtask.cardId}`, {
            childSort: (index + 1) * SORT_GAP,
          }),
        ),
      );
    } catch {
      // Revert on failure — refetch would be ideal but leave as-is for now
    }
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverId(null);
  };

  // ---------------------------
  // Subtask creation
  // ---------------------------

  const handleSubmit = React.useCallback(async () => {
    const trimmed = newTitle.trim();
    if (!trimmed || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await api.post<{ data: { cardId: string; cardNumber: number; title: string } }>(
        `/v1/boards/${boardId}/cards/${cardId}/subtasks`,
        { title: trimmed },
      );
      onSubtaskCreated(response.data);
      setNewTitle('');
      inputRef.current?.focus();
    } catch {
      setError('Failed to create subtask. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [boardId, cardId, newTitle, isSubmitting, onSubtaskCreated]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        void handleSubmit();
      }
    },
    [handleSubmit],
  );

  // ---------------------------
  // Navigation
  // ---------------------------

  const handleNavigate = React.useCallback((subtaskCardId: string) => {
    const event = new CustomEvent('navigate-to-card', {
      detail: { cardId: subtaskCardId },
      bubbles: true,
    });
    document.dispatchEvent(event);
  }, []);

  // ---------------------------
  // Toggle and delete
  // ---------------------------

  const handleToggle = React.useCallback(async (subtask: SubtaskResponseItem) => {
    if (isReadOnly) return;

    const newValue = !subtask.isComplete;
    try {
      await api.put(`/v1/boards/${boardId}/cards/${subtask.cardId}`, {
        isComplete: newValue,
      });
      onSubtaskToggled(subtask.cardId, newValue);
    } catch {
      // Toggle failed
    }
  }, [boardId, isReadOnly, onSubtaskToggled]);

  const handleDelete = React.useCallback(async (subtask: SubtaskResponseItem) => {
    try {
      await api.del(`/v1/boards/${boardId}/cards/${subtask.cardId}`);
      onSubtaskDeleted(subtask.cardId);
    } catch {
      // Delete failed
    }
  }, [boardId, onSubtaskDeleted]);

  // ---------------------------
  // Render
  // ---------------------------

  return (
    <section aria-label="Subtasks">
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Subtasks
      </h4>

      {/* Progress bar and summary */}
      {subtasks.length > 0 && (
        <div className="mb-3">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-xs text-muted-foreground" aria-live="polite">
              {progressText}
            </span>
            <span className="text-xs text-muted-foreground">
              {percentage}%
            </span>
          </div>
          <div
            className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-valuenow={progress.completed}
            aria-valuemin={0}
            aria-valuemax={progress.total}
            aria-label={progressText}
          >
            <div
              className={cn('h-full rounded-full transition-all duration-300', barColor)}
              style={{ width: `${percentage}%` }}
            />
          </div>
        </div>
      )}

      {/* Subtask list */}
      {subtasks.length > 0 ? (
        <ul role="list" aria-label="Subtask list" className="mb-2 space-y-0.5">
          {subtasks.map((subtask) => (
            <li
              key={subtask.cardId}
              role="listitem"
              draggable={!isReadOnly}
              onDragStart={() => handleDragStart(subtask.cardId)}
              onDragOver={(e) => handleDragOver(e, subtask.cardId)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, subtask.cardId)}
              onDragEnd={handleDragEnd}
              className={cn(
                'flex items-center gap-2 rounded-md px-2 py-1.5 group transition-all',
                'hover:bg-accent focus-within:bg-accent',
                draggingId === subtask.cardId && 'opacity-40',
                dragOverId === subtask.cardId && 'border-t-2 border-primary',
              )}
              aria-label={`Subtask: ${subtask.title}, ${subtask.isComplete ? 'complete' : 'incomplete'}`}
            >
              {/* Drag handle */}
              {!isReadOnly && (
                <span
                  className="shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-muted-foreground select-none text-xs"
                  aria-hidden="true"
                  title="Drag to reorder"
                >
                  ⠿
                </span>
              )}

              {/* Completion toggle */}
              <span
                onClick={(e) => { e.stopPropagation(); handleToggle(subtask); }}
                className="shrink-0 cursor-pointer"
              >
                <Checkbox
                  checked={subtask.isComplete}
                  disabled={isReadOnly}
                  aria-label={`Mark "${subtask.title}" as ${subtask.isComplete ? 'incomplete' : 'complete'}`}
                  tabIndex={-1}
                  className="pointer-events-none shrink-0"
                />
              </span>

              {/* Title and card number — clickable to navigate, double-click to edit */}
              <div
                className="min-w-0 flex-1"
              >
                {editingSubtaskId === subtask.cardId ? (
                  <input
                    ref={subtaskInputRef}
                    type="text"
                    value={editingSubtaskValue}
                    onChange={(e) => setEditingSubtaskValue(e.target.value)}
                    onKeyDown={handleSubtaskTitleKeyDown}
                    onBlur={saveSubtaskTitle}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full text-sm bg-transparent border border-input rounded px-1 py-0 focus:outline-none focus:ring-2 focus:ring-ring"
                    aria-label={`Edit title for subtask "${subtask.title}"`}
                  />
                ) : (
                  <>
                    <span
                      className={cn(
                        'text-sm cursor-pointer rounded px-1 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        subtask.isComplete && 'line-through text-muted-foreground',
                      )}
                      onDoubleClick={(e) => { e.stopPropagation(); startSubtaskEdit(subtask.cardId, subtask.title); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); startSubtaskEdit(subtask.cardId, subtask.title); } }}
                      onClick={() => handleNavigate(subtask.cardId)}
                      tabIndex={!isReadOnly && onSubtaskRenamed ? 0 : undefined}
                      role={!isReadOnly && onSubtaskRenamed ? 'button' : undefined}
                      aria-label={!isReadOnly && onSubtaskRenamed ? `Subtask: ${subtask.title}. Press Enter or double-click to edit.` : undefined}
                    >
                      {subtask.title}
                    </span>
                    <span className="ml-1.5 text-xs text-muted-foreground">
                      #{subtask.cardNumber}
                    </span>
                  </>
                )}
              </div>

              {/* Assignee control */}
              {!isReadOnly ? (
                <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                  <CardAssigneePopover
                    boardId={boardId}
                    cardId={subtask.cardId}
                    boardMembers={boardMembers}
                    currentAssignees={subtaskAssignees[subtask.cardId] ?? []}
                    isReadOnly={isReadOnly}
                    onAssignmentChange={(assignees) => handleSubtaskAssignmentChange(subtask.cardId, assignees)}
                  />
                </div>
              ) : (
                subtask.assignees.length > 0 && (
                  <div className="flex shrink-0 -space-x-1">
                    {subtask.assignees.slice(0, 3).map((assignee) => (
                      <span
                        key={assignee.userId}
                        title={assignee.displayName}
                        className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-secondary text-[10px] font-medium text-secondary-foreground ring-1 ring-background"
                      >
                        {getInitials(assignee.displayName)}
                      </span>
                    ))}
                  </div>
                )
              )}

              {/* Delete button — visible on hover */}
              {!isReadOnly && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(subtask); }}
                  className="shrink-0 opacity-0 group-hover:opacity-100 focus:opacity-100 text-muted-foreground hover:text-destructive p-0.5 rounded transition-opacity"
                  aria-label={`Delete subtask "${subtask.title}"`}
                  title="Delete subtask"
                >
                  ✕
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-2 text-xs text-muted-foreground">
          No subtasks yet.
        </p>
      )}

      {/* Inline creation input */}
      {!isReadOnly && (
        <div className="mt-2">
          <label htmlFor={`add-subtask-${cardId}`} className="sr-only">
            Add a subtask
          </label>
          <input
            id={`add-subtask-${cardId}`}
            ref={inputRef}
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add a subtask..."
            disabled={isSubmitting}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={error ? `subtask-error-${cardId}` : undefined}
            className={cn(
              'w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-card-foreground',
              'ring-offset-background placeholder:text-muted-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'disabled:opacity-50',
            )}
          />
          {error && (
            <p
              id={`subtask-error-${cardId}`}
              className="mt-1 text-xs text-destructive"
              role="alert"
            >
              {error}
            </p>
          )}
        </div>
      )}

      {/* Remove from parent confirmation dialog */}
      <AlertDialog open={!!removeFromParentPrompt} onOpenChange={(open) => { if (!open) setRemoveFromParentPrompt(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from parent card?</AlertDialogTitle>
            <AlertDialogDescription>
              {removeFromParentPrompt?.displayName} was unassigned from this subtask. Remove them from the parent card&apos;s assignees too?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDismissRemoveFromParent}>Keep on parent</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmRemoveFromParent}>Remove from parent</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
